import type { GroupPayload, WidgetPayload } from "@workspace/contracts/embed"

import { visitorKey } from "./events"
import { armTrigger } from "./rules"
import { read, write } from "./storage"

/**
 * Which of a group's members play, by mode (`GROUP_MODE_COPY`):
 *
 * - `split` - ONE member per visitor, chosen by weight and STICKY: the
 *   visitor key hashed with the group's public `salt` lands in a bucket,
 *   remembered in `localStorage` so the same browser sees the same member
 *   on every visit, even after the weights change.
 * - `sequence` - ONE member at a time over a visit: the step is remembered
 *   in `sessionStorage` and moves on when the current member's `advanceOn`
 *   trigger fires (a member with none is the end of the line).
 * - `parallel` - EVERY member, together.
 */

type Member = GroupPayload["members"][number]

const bucketKey = (groupId: string) => `usereq:split:${groupId}`
const stepKey = (groupId: string) => `usereq:seq:${groupId}`

/** FNV-1a over the visitor key and the salt: fast, spread, deterministic. */
function hash(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

function byPosition(members: readonly Member[]): Member[] {
  return [...members].sort((a, b) => a.position - b.position)
}

/** The split's member for this visitor - the one remembered, else the one the hash picks. */
export function splitMember(group: GroupPayload): Member | null {
  const members = byPosition(group.members)
  if (members.length === 0) return null

  const remembered = read("localStorage", bucketKey(group.id))
  const kept = remembered
    ? members.find((m) => m.widget.id === remembered)
    : undefined
  if (kept) return kept

  const total = members.reduce((sum, m) => sum + m.weight, 0)
  let roll = hash(`${visitorKey()}:${group.config.salt}`) % total
  let chosen = members[members.length - 1]!
  for (const member of members) {
    if (roll < member.weight) {
      chosen = member
      break
    }
    roll -= member.weight
  }
  write("localStorage", bucketKey(group.id), chosen.widget.id)
  return chosen
}

/** The sequence's current member, by the step this visit is on. */
export function sequenceMember(group: GroupPayload): Member | null {
  const members = byPosition(group.members)
  if (members.length === 0) return null
  const step = Number(read("sessionStorage", stepKey(group.id)) ?? 0)
  const index = Number.isFinite(step) ? Math.min(step, members.length - 1) : 0
  return members[index] ?? null
}

/**
 * Arm the current member's `advanceOn`; when it fires the step moves on and
 * `onAdvance` gets the next member (or nothing, at the end of the line).
 */
export function armAdvance(
  group: GroupPayload,
  current: Member,
  onAdvance: (next: Member | null) => void
): () => void {
  if (!current.advanceOn) return () => {}
  const members = byPosition(group.members)
  const at = members.findIndex((m) => m.widget.id === current.widget.id)
  return armTrigger(current.advanceOn.kind, current.advanceOn.params, () => {
    const next = at + 1 < members.length ? at + 1 : null
    if (next === null) {
      onAdvance(null)
      return
    }
    write("sessionStorage", stepKey(group.id), String(next))
    onAdvance(members[next] ?? null)
  })
}

/** Every widget the group shows right now, in position order. */
export function playingMembers(group: GroupPayload): WidgetPayload[] {
  switch (group.mode) {
    case "split": {
      const member = splitMember(group)
      return member ? [member.widget] : []
    }
    case "sequence": {
      const member = sequenceMember(group)
      return member ? [member.widget] : []
    }
    case "parallel":
      return byPosition(group.members).map((m) => m.widget)
  }
}
